import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { notificationsApi, type NotificationsResponse } from '../../api/notifications';
import { clayTapScale } from '../../constants/animations';
import { useAuthStore } from '../../store/authStore';
import { usePortalStore } from '../../store/portalStore';
import { getPortalUrl } from '../../lib/portal';
import { NotificationItem } from './NotificationItem';
import styles from './NotificationBell.module.css';

const POLL_INTERVAL_MS = 60_000;

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<NotificationsResponse | null>(null);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [readOpen, setReadOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const fetchMe = useAuthStore((s) => s.fetchMe);
  const portal = usePortalStore((s) => s.portal);

  const load = useCallback(async () => {
    try {
      const result = await notificationsApi.list();
      setData(result);
    } catch {
      // Polling failure is non-critical; quietly skip.
    }
  }, []);

  // Initial load
  useEffect(() => {
    void load();
  }, [load]);

  // Poll when tab is visible
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  // Refetch on tab focus so the user sees fresh state when returning
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [load]);

  // Outside click closes the dropdown
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleToggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next && (data?.unreadCount ?? 0) > 0) {
        notificationsApi.markAllRead().catch(() => {/* non-critical */});
        setData((d) => {
          if (!d) return d;
          const now = new Date().toISOString();
          return {
            unreadCount: 0,
            items: d.items.map((it) => (it.readAt ? it : { ...it, readAt: now })),
          };
        });
      }
      return next;
    });
  };

  const handleAccept = async (id: string) => {
    setActioningId(id);
    try {
      await notificationsApi.acceptManagerInvite(id);
      toast.success('Invitation accepted');
      // Accepting grants creator-portal access — refresh the session so the UI
      // (creator-mode switch, bell gating, etc.) reflects it immediately.
      await fetchMe();
      // A viewer who just accepted belongs in the creator portal, where the
      // event-management UI lives. Hand them off there.
      if (portal === 'viewer') {
        window.location.href = getPortalUrl('creator');
        return;
      }
      await load();
      window.dispatchEvent(new CustomEvent('pm:events-refresh'));
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { error?: string } } };
      if (e.response?.status === 410) {
        toast.error(e.response.data?.error ?? 'This invitation is no longer available');
        await load();
      } else {
        toast.error(e.response?.data?.error ?? 'Failed to accept invitation');
      }
    } finally {
      setActioningId(null);
    }
  };

  const handleDecline = async (id: string) => {
    setActioningId(id);
    try {
      await notificationsApi.declineManagerInvite(id);
      toast.success('Invitation declined');
      await load();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error ?? 'Failed to decline invitation');
    } finally {
      setActioningId(null);
    }
  };

  const unreadCount = data?.unreadCount ?? 0;
  const items = data?.items ?? [];
  const pending = items.filter((i) => i.actionTaken === null);
  const actioned = items.filter((i) => i.actionTaken !== null);
  // Toggle only shown when both sections coexist; otherwise actioned list is always visible.
  const hasBoth = pending.length > 0 && actioned.length > 0;

  return (
    <div className={styles['wrap']} ref={wrapRef}>
      <motion.button
        type="button"
        onClick={handleToggle}
        className={styles['bell-btn']}
        whileHover={{ scale: 1.05 }}
        whileTap={clayTapScale}
        transition={{ type: 'spring', stiffness: 500, damping: 20 }}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Bell className={styles['bell-icon']} />
        {unreadCount > 0 && (
          <span className={styles['bell-badge']} aria-hidden="true">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            className={styles['dropdown']}
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -8 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            role="menu"
          >
            <div className={styles['dropdown-header']}>
              <span className={styles['dropdown-title']}>Notifications</span>
            </div>

            <div className={styles['dropdown-body']}>
              {items.length === 0 ? (
                <div className={styles['empty']}>
                  <p className={styles['empty-title']}>You&apos;re all caught up</p>
                  <p className={styles['empty-text']}>
                    Invitations and updates will appear here.
                  </p>
                </div>
              ) : (
                <>
                  {/* Pending — action required */}
                  {pending.length > 0 && (
                    <ul className={styles['list']}>
                      <AnimatePresence initial={false}>
                        {pending.map((item) => (
                          <motion.li
                            key={item._id}
                            className={styles['list-item']}
                            layout
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                          >
                            <NotificationItem
                              notification={item}
                              actioning={actioningId === item._id}
                              onAccept={() => handleAccept(item._id)}
                              onDecline={() => handleDecline(item._id)}
                            />
                          </motion.li>
                        ))}
                      </AnimatePresence>
                    </ul>
                  )}

                  {/* Actioned — read / earlier */}
                  {actioned.length > 0 && (
                    <div className={styles['read-section']}>
                      {hasBoth ? (
                        <button
                          className={styles['read-toggle']}
                          onClick={() => setReadOpen((o) => !o)}
                          aria-expanded={readOpen}
                        >
                          <span>Earlier ({actioned.length})</span>
                          <ChevronDown
                            className={[
                              styles['read-chevron'],
                              readOpen ? styles['read-chevron-open'] : '',
                            ].filter(Boolean).join(' ')}
                          />
                        </button>
                      ) : (
                        <div className={styles['read-heading']}>Earlier</div>
                      )}

                      <AnimatePresence initial={false}>
                        {(!hasBoth || readOpen) && (
                          <motion.ul
                            className={styles['list']}
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            style={{ overflow: 'hidden' }}
                            transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                          >
                            {actioned.map((item) => (
                              <li key={item._id} className={styles['list-item']}>
                                <NotificationItem
                                  notification={item}
                                  actioning={actioningId === item._id}
                                  onAccept={() => handleAccept(item._id)}
                                  onDecline={() => handleDecline(item._id)}
                                />
                              </li>
                            ))}
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
